import { ChevronsUpDown, Loader2 } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar/sidebar-menu';
import { useSidebar } from '@/components/ui/sidebar/hooks/use-sidebar';
import { sidebarIconButton } from '@/components/ui/sidebar/helpers/sidebar-theme';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { WorkspaceSwitcherContent } from '@/features/workspace/components/workspace-switcher/workspace-switcher-content';
import { WORKSPACE_SWITCHER_LAYOUT, getWorkspaceSwitcherPopoverHeight } from '@/features/workspace/helpers/workspace-switcher-layout';
import { useWorkspaceSwitcher } from '@/features/workspace/hooks/use-workspace-switcher';
import { cn } from '@/lib/utils';

export function WorkspaceSwitcher() {
  const { isMobile, state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const {
    accounts,
    filteredAccounts,
    currentAccount,
    isLoading,
    searchQuery,
    setSearchQuery,
    handleSwitchAccount,
    handleAddNewCompany,
    open,
    setOpen,
    user,
    signOut,
    isSigningOut,
    planName,
  } = useWorkspaceSwitcher();

  const calculatedHeight = getWorkspaceSwitcherPopoverHeight({
    accountCount: accounts.length,
    isMobile,
  });

  const contentProps = {
    accounts,
    filteredAccounts,
    currentAccount,
    isLoading,
    searchQuery,
    onSearchChange: setSearchQuery,
    onSelectAccount: handleSwitchAccount,
    onAddNewCompany: handleAddNewCompany,
    itemHeight: WORKSPACE_SWITCHER_LAYOUT.ACCOUNT_ITEM_HEIGHT,
    user,
    onSignOut: signOut,
    isSigningOut,
  };

  const listboxId = 'workspace-switcher-listbox';

  const popoverContent = (
    <PopoverContent
      id={listboxId}
      role="listbox"
      className="PopoverContent flex w-72 flex-col overflow-hidden rounded-lg border border-border-default bg-surface-raised p-0 shadow-raised"
      style={{
        height: `${calculatedHeight}px`,
        ...(isMobile ? { overscrollBehavior: 'contain' as const } : {}),
      }}
      align="start"
      side="bottom"
      sideOffset={4}
      onOpenAutoFocus={isMobile ? e => e.preventDefault() : undefined}
    >
      <WorkspaceSwitcherContent {...contentProps} />
    </PopoverContent>
  );

  if (isCollapsed) {
    return (
      <SidebarMenu>
        <SidebarMenuItem className="flex justify-center">
          <Popover open={open} onOpenChange={setOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-haspopup="listbox"
                    aria-label="Switch workspace"
                    className={cn(sidebarIconButton(), open && 'bg-neutral-interaction-selected')}
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-sidebar-primary text-xs font-semibold text-sidebar-primary-foreground">
                      {currentAccount?.accountName?.charAt(0).toUpperCase() ?? 'W'}
                    </div>
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div className="text-sm font-medium">
                  {currentAccount?.accountName || 'Switch workspace'}
                </div>
                {planName && <div className="text-xs text-content-secondary">{planName}</div>}
              </TooltipContent>
            </Tooltip>
            {popoverContent}
          </Popover>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-haspopup="listbox"
              className="w-full overflow-hidden px-1.5 data-[state=open]:bg-neutral-interaction-selected"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sidebar-primary text-sm font-semibold text-sidebar-primary-foreground">
                  {currentAccount?.accountName?.charAt(0).toUpperCase() ?? 'W'}
                </div>
                <div className="h-6 w-px bg-border" />
                <div className="grid min-w-0 flex-1 text-left text-sm leading-tight text-inherit">
                  <span
                    className="truncate font-medium leading-4"
                    title={currentAccount?.accountName}
                  >
                    {currentAccount?.accountName}
                  </span>
                  <span
                    className="truncate text-xs leading-3.5 text-content-secondary"
                    title={planName}
                  >
                    {planName}
                  </span>
                </div>
              </div>
              {isLoading ? (
                <Loader2 className="ml-auto h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 text-content-tertiary" />
              )}
            </SidebarMenuButton>
          </PopoverTrigger>
          {popoverContent}
        </Popover>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
